import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { packagesAPI } from '../../services/api';
import { Button } from '../ui';
import { handleApiError } from '../../utils/helpers';

const PackageFormModal = ({ package: pkg, clientId, onClose, onSaved }) => {
  const { isRTL } = useLanguage();
  const isEditing = !!pkg;
  const isClientSpecific = !!clientId && !isEditing;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState(() => {
    // Helper function to migrate blog to website
    const migratePlatforms = (platforms) => {
      if (!platforms || !Array.isArray(platforms)) return [];
      return platforms.map(p => p === 'blog' ? 'website' : p);
    };
    
    return {
      name: pkg?.name || '',
      name_ar: pkg?.name_ar || '',
      description: pkg?.description || '',
      description_ar: pkg?.description_ar || '',
      platform: (() => {
        // Migrate blog to website for backward compatibility
        if (pkg?.platform === 'blog') return 'website';
        return pkg?.platform || '';
      })(),
      platforms: (() => {
        // If package has platforms array, migrate blog to website
        if (pkg?.platforms && Array.isArray(pkg.platforms)) {
          return migratePlatforms(pkg.platforms);
        }
        // If single platform (old way), convert blog to website
        if (pkg?.platform && pkg.platform !== 'all') {
          return [pkg.platform === 'blog' ? 'website' : pkg.platform];
        }
        return [];
      })(),
      content_type: pkg?.content_type || '',
      post_limit: pkg?.post_limit || 10,
      duration_days: pkg?.duration_days || '',
      price: pkg?.price || 0,
      currency: pkg?.currency || 'SAR',
      is_active: pkg?.is_active ?? true,
    };
  });

  const platforms = [
    { value: 'instagram', label: { en: 'Instagram', ar: 'إنستقرام' } },
    { value: 'tiktok', label: { en: 'TikTok', ar: 'تيك توك' } },
    { value: 'snapchat', label: { en: 'Snapchat', ar: 'سناب شات' } },
    { value: 'twitter', label: { en: 'Twitter/X', ar: 'تويتر' } },
    { value: 'facebook', label: { en: 'Facebook', ar: 'فيسبوك' } },
    { value: 'linkedin', label: { en: 'LinkedIn', ar: 'لينكد إن' } },
    { value: 'website', label: { en: 'Website (includes Blog)', ar: 'موقع (يشمل المدونة)' } },
  ];

  const contentTypes = [
    { value: '', label: { en: 'All Types', ar: 'جميع الأنواع' } },
    { value: 'image_post', label: { en: 'Image Post', ar: 'بوست صورة' } },
    { value: 'video_post', label: { en: 'Video Post', ar: 'بوست فيديو' } },
    { value: 'reel', label: { en: 'Reel', ar: 'ريلز' } },
    { value: 'story', label: { en: 'Story', ar: 'ستوري' } },
    { value: 'carousel', label: { en: 'Carousel', ar: 'كاروسيل' } },
    { value: 'banner', label: { en: 'Banner', ar: 'بانر' } },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const handlePlatformToggle = (platformValue) => {
    setFormData(prev => {
      const currentPlatforms = prev.platforms || [];
      const newPlatforms = currentPlatforms.includes(platformValue)
        ? currentPlatforms.filter(p => p !== platformValue)
        : [...currentPlatforms, platformValue];
      
      return {
        ...prev,
        platforms: newPlatforms,
        // For backward compatibility, set platform to first item if only one, or null if multiple/none
        platform: newPlatforms.length === 1 ? newPlatforms[0] : (newPlatforms.length > 1 ? null : '')
      };
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        post_limit: parseInt(formData.post_limit),
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        price: parseFloat(formData.price) || 0,
        platforms: formData.platforms || [], // Send platforms array
        platform: formData.platform || null, // Keep for backward compatibility
        content_type: formData.content_type || null,
      };

      if (isEditing) {
        await packagesAPI.update(pkg.id, submitData);
      } else if (isClientSpecific) {
        await packagesAPI.createClientSpecific(clientId, submitData);
      } else {
        await packagesAPI.create(submitData);
      }

      onSaved();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing 
                ? (isRTL ? 'تعديل الباقة' : 'Edit Package')
                : isClientSpecific
                ? (isRTL ? 'إنشاء باقة مخصصة للعميل' : 'Create Custom Package for Client')
                : (isRTL ? 'إضافة باقة جديدة' : 'Add New Package')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              type="button"
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Section 1: Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </h3>
            <div className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'اسم الباقة (إنجليزي)' : 'Package Name (English)'} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input"
                placeholder="e.g., 30 Posts Package"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'اسم الباقة (عربي)' : 'Package Name (Arabic)'}
              </label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleChange}
                className="input"
                placeholder="مثال: باقة 30 بوست"
                dir="rtl"
              />
            </div>
              </div>

              {/* Description Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الوصف (إنجليزي)' : 'Description (English)'}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                className="textarea"
                placeholder="Package description..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الوصف (عربي)' : 'Description (Arabic)'}
              </label>
              <textarea
                name="description_ar"
                value={formData.description_ar}
                onChange={handleChange}
                rows="2"
                className="textarea"
                placeholder="وصف الباقة..."
                dir="rtl"
              />
            </div>
              </div>
            </div>
          </div>

          {/* Section 2: Package Configuration */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'إعدادات الباقة' : 'Package Configuration'}
            </h3>
            <div className="space-y-4">
              {/* Platforms (Multiple Selection) & Content Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'المنصات' : 'Platforms'}
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                {platforms.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    {isRTL ? 'لا توجد منصات متاحة' : 'No platforms available'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {platforms.map(p => (
                      <label
                        key={p.value}
                        className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.platforms?.includes(p.value) || false}
                          onChange={() => handlePlatformToggle(p.value)}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className={`text-sm text-gray-700 ${isRTL ? 'mr-2' : 'ml-2'}`}>
                          {isRTL ? p.label.ar : p.label.en}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {isRTL 
                  ? 'اختر واحدة أو أكثر من المنصات. اتركه فارغاً ليشمل جميع المنصات.' 
                  : 'Select one or more platforms. Leave empty for all platforms.'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'نوع المحتوى' : 'Content Type'}
              </label>
              <select
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
                className="input"
              >
                {contentTypes.map(t => (
                  <option key={t.value} value={t.value}>
                    {isRTL ? t.label.ar : t.label.en}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {isRTL ? 'اتركه فارغاً ليشمل جميع الأنواع' : 'Leave empty for all types'}
              </p>
            </div>
              </div>
            </div>
          </div>

          {/* Section 3: Limits & Duration */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'الحدود والمدة' : 'Limits & Duration'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'عدد البوستات' : 'Posts Limit'} *
              </label>
              <input
                type="number"
                name="post_limit"
                value={formData.post_limit}
                onChange={handleChange}
                required
                min="1"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'مدة الباقة (بالأيام)' : 'Duration (days)'}
              </label>
              <input
                type="number"
                name="duration_days"
                value={formData.duration_days}
                onChange={handleChange}
                min="1"
                className="input"
                placeholder={isRTL ? 'اتركه فارغاً لباقة غير محدودة' : 'Leave empty for unlimited'}
              />
            </div>
            </div>
          </div>

          {/* Section 4: Pricing */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'التسعير' : 'Pricing'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'السعر' : 'Price'}
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'العملة' : 'Currency'}
              </label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="input"
              >
                <option value="SAR">SAR (ريال سعودي)</option>
                <option value="USD">USD (دولار)</option>
                <option value="AED">AED (درهم)</option>
              </select>
            </div>
            </div>
          </div>

          {/* Section 5: Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'حالة الباقة' : 'Package Status'}
            </h3>
            <div className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              id="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-gray-700`}>
              {isRTL ? 'الباقة نشطة (متاحة للاختيار)' : 'Package is active (available for selection)'}
            </label>
          </div>
          </div>

          {/* Actions */}
          <div className={`flex items-center justify-end pt-4 border-t border-gray-200 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading}
            >
              {isEditing 
                ? (isRTL ? 'حفظ التغييرات' : 'Save Changes')
                : isClientSpecific
                ? (isRTL ? 'إنشاء وتكليف للعميل' : 'Create & Assign to Client')
                : (isRTL ? 'إنشاء الباقة' : 'Create Package')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PackageFormModal;





